<script setup lang="ts">
import { defineLayoutOfProduct } from '@/utils/defineLayoutOfProduct'

import { useCityStore } from '@/store/city/city'

import { Product } from '@/types/api/api-v3/product'
import { EcommerceEventName } from '@/types/plugins/yandex-metrika'
import { NAME_PARENT_COMPONENT } from '@/types/products'

import ProductContent from '@/components/Product/ProductItem/ProductContent/ProductContent.vue'
import ProductImage from '@/components/Product/ProductItem/ProductImage/ProductImage.vue'
import ProductTags from '@/components/Product/ProductItem/ProductTags/ProductTags.vue'

import { mapEcommerceData } from '@/services/metrics/utils/ecommerce'

const emit = defineEmits(['openDialogProduct'])

type Props = {
  product: Product
  parentComponent: string
}
const props = defineProps<Props>()

const cityStore = useCityStore()

const { isMobileOrTablet } = useDevice()

const { $metrics } = useNuxtApp()

const productClasses = computed(() => {
  return [
    'product',
    {
      'product--main-desktop':
        NAME_PARENT_COMPONENT.MAIN_DESKTOP === props.parentComponent,
      'product--main-mobile':
        NAME_PARENT_COMPONENT.MAIN_MOBILE === props.parentComponent,
      'product--dialog': NAME_PARENT_COMPONENT.DIALOG === props.parentComponent,
      'product--dialog-mobile':
        NAME_PARENT_COMPONENT.DIALOG === props.parentComponent &&
        isMobileOrTablet,
      'product--page': NAME_PARENT_COMPONENT.PAGE === props.parentComponent,
    },
  ]
})

const isMainPage = computed(
  () =>
    NAME_PARENT_COMPONENT.MAIN_DESKTOP === props.parentComponent ||
    NAME_PARENT_COMPONENT.MAIN_MOBILE === props.parentComponent,
)

const wrapperComponent = computed(() => (isMainPage.value ? 'a' : 'div'))

const wrapperAttrs = computed(() => {
  return isMainPage.value
    ? {
        href: `/product/${props.product.slug}/`,
      }
    : {}
})

const isHighLight = computed(() => {
  return props.product.tags.find((item) => item.as_highlight)
})

const isShowHighLight = computed(
  () =>
    NAME_PARENT_COMPONENT.MAIN_DESKTOP === props.parentComponent ||
    NAME_PARENT_COMPONENT.MAIN_MOBILE === props.parentComponent,
)

const currentModification = ref(props.product?.modifications[0] || {}) // текущая модификация товара

watch(
  () => props.product,
  (newProduct) => {
    currentModification.value = newProduct?.modifications[0] || {}
  },
)

const currencyCode = computed(() => cityStore.currencyCode)

const openDialogProduct = () => {
  emit('openDialogProduct', props.product)

  const ecommerceData = mapEcommerceData(
    currentModification.value,
    EcommerceEventName.DETAIL,
    currencyCode?.value || '',
  )

  $metrics?.eventViewedProduct({ ym: ecommerceData })
}

const getProductMicrolayouts = defineLayoutOfProduct(props.parentComponent)

const itemScopeProductItem = getProductMicrolayouts(true)
const itemTypeProductItem = getProductMicrolayouts('http://schema.org/Product')

const changeModification = (modificationId: number) => {
  // меняем текущую модификацию продукта путем поиска по id модификации
  currentModification.value =
    props.product.modifications.find(
      (modification) => modification.id === modificationId,
    ) || props.product.modifications[0]
}
</script>

<template>
  <div
    :itemscope="itemScopeProductItem"
    :itemtype="itemTypeProductItem"
    :class="productClasses"
  >
    <product-image
      :product="product"
      :current-modification="currentModification"
      :parent-component="parentComponent"
    />

    <product-tags
      v-if="product.tags"
      :product="product"
      :parent-component="parentComponent"
    />

    <div class="product__sale">
      <span
        v-if="isHighLight && isShowHighLight"
        class="product__sale--text"
        :style="{ 'background-color': isHighLight.hover_background_color }"
      >
        {{ isHighLight.name }}
      </span>
    </div>

    <product-content
      :product="product"
      :wrapper-attrs="wrapperAttrs"
      :wrapper-component="wrapperComponent"
      :is-main-page="isMainPage"
      :current-modification="currentModification"
      :parent-component="parentComponent"
      @open-dialog-product="openDialogProduct"
      @change-modification="changeModification"
    />
  </div>
</template>

<style scoped lang="scss">
@mixin product-main-desktop {
  position: relative;
  display: grid;
  grid-template-rows: repeat(2, max-content) 1fr;
  grid-template-columns: 100%;
  width: 273px;
  cursor: default;

  .product__sale {
    display: flex;
    grid-area: 2 / 1 / 3 / 3;
    height: 20px;

    &--text {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 20px;
      font-size: 12px;
      font-weight: bold;
      line-height: 14px;
      color: var(--ui-white);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      background: #00c304;
      border-radius: 20px;
    }
  }
}

@mixin product-main-mobile {
  position: relative;
  display: grid;
  grid-template-rows: repeat(2, max-content) 1fr;
  grid-template-columns: 100%;
  padding: 12px 12px 16px;
  background: var(--ui-white);
  border-radius: 16px;
  box-shadow: 0 6px 30px rgba(50, 52, 72, 0.08);

  .product__sale {
    display: flex;
    grid-area: 2 / 1 / 3 / 2;
    height: 20px;

    &--text {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 20px;
      font-size: 12px;
      font-weight: bold;
      line-height: 12px;
      color: var(--ui-white);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      background: #00c304;
      border-radius: 20px;

      @include media('<tablet') {
        font-size: 8px;
        font-weight: 800;
        line-height: 16px;
      }
    }
  }
}

@mixin product-dialog {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;

  @include media('<=tablet') {
    align-items: flex-start;
    justify-content: flex-start;
    height: initial;
  }

  &-mobile {
    align-items: flex-start !important;
    justify-content: flex-start !important;
    height: initial;
  }
}

@mixin product-page {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 0 max(452px);
  gap: 0 35px;
  align-items: center;
  margin: 50px 0 90px;

  @include media('<=desktop-mini') {
    margin: 26px 0;
  }

  @include media('<=desktop') {
    grid-template-columns: 1fr;
    padding-bottom: 0;
  }
}

.product--main-desktop {
  @include product-main-desktop;
}

.product--main-mobile {
  @include product-main-mobile;
}

.product--dialog {
  @include product-dialog;
}

.product--page {
  @include product-page;
}
</style>
